import { settings } from './store';

// All sounds are synthesized with WebAudio, so nothing needs to be downloaded (works offline).

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (!settings.get().sound) return null;
  try {
    ctx ??= new AudioContext();
    return ctx;
  } catch {
    return null;
  }
}

function tone(freq: number, dur: number, type: OscillatorType = 'sine', gain = 0.15, slide = 0) {
  const c = audio();
  if (!c) return;
  const t = c.currentTime;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  if (slide) o.frequency.exponentialRampToValueAtTime(Math.max(20, freq + slide), t + dur);
  g.gain.setValueAtTime(gain, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  o.connect(g).connect(c.destination);
  o.start(t);
  o.stop(t + dur);
}

function noise(dur: number, gain = 0.3, lowpass = 800) {
  const c = audio();
  if (!c) return;
  const len = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 2;
  const src = c.createBufferSource();
  src.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = 'lowpass';
  f.frequency.value = lowpass;
  const g = c.createGain();
  g.gain.value = gain;
  src.connect(f).connect(g).connect(c.destination);
  src.start();
}

export const sfx = {
  move: () => tone(420, 0.07, 'triangle', 0.2),
  capture: () => { tone(260, 0.1, 'square', 0.12); noise(0.08, 0.15, 2000); },
  check: () => { tone(880, 0.12, 'sine', 0.15); setTimeout(() => tone(660, 0.15, 'sine', 0.12), 90); },
  explode: () => { noise(0.7, 0.5, 600); tone(90, 0.5, 'sawtooth', 0.2, -60); },
  fire: () => { noise(0.5, 0.25, 1500); tone(200, 0.4, 'sawtooth', 0.06, 200); },
  whoosh: () => noise(0.35, 0.2, 900),
  mine: () => tone(150, 0.12, 'square', 0.08),
  brilliant: () => { tone(784, 0.12, 'sine', 0.12); setTimeout(() => tone(988, 0.12, 'sine', 0.12), 90); setTimeout(() => tone(1319, 0.25, 'sine', 0.1), 180); },
  blunder: () => tone(180, 0.35, 'triangle', 0.12, -60),
  /** Air-raid siren: four rising wails. */
  alarm: () => { for (let i = 0; i < 4; i++) setTimeout(() => tone(520, 0.5, 'sawtooth', 0.11, 380), i * 550); },
  plane: () => { noise(1.6, 0.12, 300); tone(70, 1.6, 'sawtooth', 0.05, 30); },
  nuke: () => { noise(2.2, 0.7, 400); tone(55, 1.8, 'sawtooth', 0.25, -35); setTimeout(() => noise(1.2, 0.3, 200), 300); },
  end: () => { tone(523, 0.15); setTimeout(() => tone(659, 0.15), 140); setTimeout(() => tone(784, 0.3), 280); },
};
